import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { motion, useMotionValue, useTransform } from 'framer-motion';
import { Box, Typography, Paper, useTheme, Dialog, DialogTitle, DialogContent, DialogActions, Button } from '@mui/material';
import { Factory, Pending, LocalShipping } from '@mui/icons-material';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import Grid from '@mui/material/Grid2';
import WatchLaterIcon from '@mui/icons-material/WatchLater';
import { getAdminDashboardAction } from '../../store/slices/adminDashboard';
import { useNavigate } from 'react-router-dom';

const DashboardCard = ({ title, value, Icon, color, delay, backgroundColor, IconColor }) => {
  const theme = useTheme();
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const rotateX = useTransform(y, [-100, 100], [30, -30]);
  const rotateY = useTransform(x, [-100, 100], [-30, 30]);

  const handleMouseMove = (event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    x.set(event.clientX - centerX);
    y.set(event.clientY - centerY);
  };

  const handleMouseLeave = () => {
    x.set(0);
    y.set(0);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay }}
      style={{
        rotateX,
        rotateY,
        transformStyle: "preserve-3d",
        perspective: 1000,
      }}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      whileHover={{ scale: 1.05 }}
    >
      <Paper
        elevation={3}
        sx={{
          p: 3,
          borderRadius: 2,
          backgroundColor: backgroundColor,
          border: `1px solid ${theme.palette.grey[100]}`,
          cursor: 'pointer',
          transition: 'transform 0.2s',
        }}
      >
        <motion.div style={{ transform: "translateZ(40px)" }}>
          <Box display="flex" alignItems="center">
            <Box
              sx={{
                p: 1.5,
                borderRadius: '50%',
                backgroundColor: color,
                transition: 'transform 0.2s',
                '&:hover': {
                  transform: 'scale(1.1)'
                }
              }}
            >
              <Icon style={{ height: 24, width: 24, color: IconColor }} />
            </Box>
            <Box ml={2}>
              <Typography variant="body2" color="textSecondary" sx={{ fontFamily: 'inter', color: '#718EBF', }}>
                {title}
              </Typography>
              <Typography variant="h5" fontWeight="600" color="textPrimary">
                {value}
              </Typography>
            </Box>
          </Box>
        </motion.div>
      </Paper>
    </motion.div>
  );
};

const SubscriptionExpiredDialog = ({ open, onContactSales, onLogout }) => {
  return (
    <Dialog 
      open={open} 
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{ fontWeight: 'bold', color: '#343C6A' }}>
        Subscription Expired
      </DialogTitle>
      <DialogContent>
        <Typography variant="body1" sx={{ mb: 2 }}>
          Your subscription has expired. Please contact our sales team to renew your subscription and continue using our services.
        </Typography>
        <Typography 
          variant="body2" 
          sx={{ 
            backgroundColor: '#f8f9fa', 
            p: 2, 
            borderRadius: 1, 
            borderLeft: '4px solid #396AFF',
            color: '#666'
          }}
        >
          <strong>Note:</strong> After your successful subscription renewal, please make sure to logout and login again to activate your new subscription.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
        <Button 
          variant="contained" 
          color="primary" 
          onClick={onContactSales}
          sx={{ 
            backgroundColor: '#396AFF',
            '&:hover': { backgroundColor: '#2a4fcc' }
          }}
        >
          Contact Sales Team
        </Button>
        <Button 
          variant="outlined" 
          color="error" 
          onClick={onLogout}
        >
          Logout
        </Button>
      </DialogActions>
    </Dialog>
  );
};


const Dashboard = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [showExpiredDialog, setShowExpiredDialog] = useState(false);
  const { successData: dashboardData, error, errorInfo } = useSelector((state) => state.adminDashboardReducer);

  useEffect(() => {
    // Check subscription status from localStorage
    const checkSubscriptionStatus = () => {
      try {
        const userDetails = localStorage.getItem('userDetails');
        console.log('userDetails', userDetails);
        
        if (userDetails) { 
          const parsedData = JSON.parse(userDetails);
          console.log('subscriptionData', parsedData); 
          if (parsedData.Client && parsedData.Client.subscription_status === 'expired') {
            setShowExpiredDialog(true);
          }
        }
      } catch (error) {
        console.error('Error checking subscription status:', error);
      }      
    };

    checkSubscriptionStatus();
    dispatch(getAdminDashboardAction());
  }, [dispatch]);

  const handleContactSales = () => {
    window.location.href = 'mailto:sales@clearbiz.com?subject=Subscription Renewal Request';
  };

  const handleLogout = () => {
    localStorage.clear();
    navigate('/');
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20, rotateX: -20 },
    visible: {
      opacity: 1,
      y: 0,
      rotateX: 0,
      transition: { type: "spring", stiffness: 100 },
    },
  };

  return (
    <>
      <SubscriptionExpiredDialog 
        open={showExpiredDialog}
        onContactSales={handleContactSales}
        onLogout={handleLogout}
      />
      
      <motion.div
        initial="hidden"
        animate="visible"
        variants={containerVariants}
        style={{ perspective: 1000 }}
      >
        <Box sx={{ mb: 3, ml: { xs: '10%', sm: '10%', md: '25%' }, mt: { xs: '22%', sm: '20%', md: '10%' } }}>
          <motion.div variants={itemVariants}>
            <Typography variant="h4" fontWeight="bold" color="textPrimary" style={{ transform: 'translateZ(0)', color: '#343C6A', fontFamily: 'Mont' }}>
              Total Orders
            </Typography>
          </motion.div>
        </Box>

        <Box sx={{ mb: { xs: '10%', sm: '0%' }, flexWrap: 'wrap', justifyContent: 'center' }}>
          <Grid container spacing={2} sx={{ ml: { xs: 3, sm: '10%', md: '25%' }, mt: { xs: '2%', sm: '2%' } }}>
            <Grid size={{ xs: 11, md: 4, lg: 3.7 }}>
              <DashboardCard title="Total Orders" value={dashboardData?.data.total_orders || 0} Icon={Pending} IconColor='#396AFF' color="#E7EDFF" backgroundColor="#FFE0E0" delay={0.3} />
            </Grid>
            <Grid size={{ xs: 11, md: 4, lg: 3.7 }}>
              <DashboardCard title="Pending" value={dashboardData?.data.pending || 0} Icon={Pending} IconColor='#396AFF' color="#E7EDFF" backgroundColor="#FFE0E0" delay={0.3} />
            </Grid>
            <Grid size={{ xs: 11, md: 4, lg: 3.7 }}>
              <DashboardCard title="Progress" value={dashboardData?.data.progress || 0} Icon={WatchLaterIcon} IconColor='#FFBB38' color="#FFF5D9" backgroundColor="#EAEAEA" delay={0.4} />
            </Grid>
          </Grid>
          <Grid container spacing={2} sx={{ ml: { xs: 3, sm: '10%', md: '25%' }, mt: 3 }}>
            <Grid size={{ xs: 11, md: 4, lg: 3.7 }}>
              <DashboardCard title="Manufactured" value={dashboardData?.data.manufactured || 0} Icon={Factory} IconColor='#FFA7B1' color="#FAFFE7" backgroundColor="#DBE1F0" delay={0.1} />
            </Grid>
            <Grid size={{ xs: 11, md: 4, lg: 3.7 }}>
              <DashboardCard title="Tested" value={dashboardData?.data.tested || 0} Icon={AssignmentTurnedInIcon} IconColor='#9051F4' color="#B68CC7" backgroundColor="#EAEAEA" delay={0.2} />
            </Grid>
            <Grid size={{ xs: 11, md: 4, lg: 3.7 }}>
              <DashboardCard title="Delivered" value={dashboardData?.data.delivered || 0} Icon={LocalShipping} IconColor='#70CB01' color="#FFE0EB" backgroundColor="#CBFFD7" delay={0.1} />
            </Grid>
          </Grid>
        </Box>
      </motion.div>
    </>
  );
};

export default Dashboard;
